import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import StatCard from "../StatCard";
import GA4Panel from "./GA4Panel";
import TabFreshnessNote from "./TabFreshnessNote";
import {
  Award, Link2, Search, TrendingUp, BarChart3, ArrowDown, ArrowUp, Sparkles, Plus, Minus,
  ExternalLink, ChevronRight,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  BarChart, Bar, Area, AreaChart,
} from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

function fmtNum(n) {
  if (n == null) return "—";
  if (n >= 1e9) return (n / 1e9).toFixed(1) + "B";
  if (n >= 1e6) return (n / 1e6).toFixed(1) + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(1) + "K";
  return String(n);
}

export default function OverviewTab({ client }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  // AI Insights teaser — fetched silently; renders only when an insight exists
  const [aiTeaser, setAiTeaser] = useState(null);
  // Which mover bucket dialog is open ("up" | "down" | "new" | "lost" | null)
  const [moverDialog, setMoverDialog] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (alive) {
        setLoading(true);
        setError(null);
      }
      try {
        const [ov, snap] = await Promise.all([
          api.post(`/seo/domain-overview?client_id=${client.id}`, {
            domain: client.domain,
            location_name: client.location_name,
            language_name: client.language_name,
          }),
          api.get(`/clients/${client.id}/rank-snapshots?limit=24`).catch(() => ({ data: { snapshots: [] } })),
        ]);
        if (!alive) return;
        setData(ov.data);
        setSnapshots(snap.data?.snapshots || []);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Failed to load");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [client.id, client.domain, client.location_name, client.language_name]);

  // AI Insights teaser — fetch the latest cached insight silently. We never trigger
  // generation here (that lives on the AI Insights tab) — this just surfaces the
  // existing brief as a clickable nudge so users notice the new tab.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: payload } = await api.get(`/clients/${client.id}/ai-insights`);
        if (alive) setAiTeaser(payload?.insight || null);
      } catch {
        if (alive) setAiTeaser(null);
      }
    })();
    return () => { alive = false; };
  }, [client.id]);

  if (loading)
    return <div className="text-sm text-[#a0aec0] font-mono-tight">Fetching overview from DataForSEO…</div>;
  if (error)
    return <div className="text-sm text-[#f56565]">Error: {error}</div>;

  const ov = data?.overview?.items?.[0]?.metrics?.organic || {};
  const bl = data?.backlinks?.items?.[0] || data?.backlinks || {};
  const hist = data?.historical?.items || [];
  const moz = data?.moz || null;

  // Domain Rank: prefer DataForSEO backlinks rank, else fall back to Moz Domain Authority.
  // Referring Domains: prefer DFS, else Moz `root_domains_to_root_domain`.
  const domainRank = bl.rank != null ? bl.rank : (moz?.domain_authority ?? null);
  const domainRankHint = bl.rank != null
    ? "DataForSEO 0–1000"
    : moz?.domain_authority != null
      ? "Moz Domain Authority 0–100"
      : "Backlinks API · enable on DataForSEO";
  const referringDomains = bl.referring_domains != null ? bl.referring_domains : (moz?.root_domains_to_root_domain ?? null);
  const referringDomainsHint = bl.referring_domains != null
    ? null
    : moz?.root_domains_to_root_domain != null
      ? "via Moz Links API"
      : "Backlinks API · enable on DataForSEO";

  // Build trendData from tracked-keyword snapshots (oldest -> newest).
  // Each point: { date, tracked: total kws, ranking: top-100 count, top10 }
  const trackedTrend = [...snapshots].reverse().map((s) => {
    const res = s.results || [];
    const ranking = res.filter((r) => r.position != null).length;
    const top10 = res.filter((r) => r.position != null && r.position <= 10).length;
    return {
      date: new Date(s.at).toLocaleDateString(undefined, { day: "2-digit", month: "short" }),
      tracked: res.length,
      ranking,
      top10,
    };
  });

  // Fallback to historical domain stats if there are no tracked snapshots yet
  const trendData = trackedTrend.length > 0
    ? trackedTrend
    : hist.slice(0, 24).reverse().map((h) => ({
        date: `${String(h.month).padStart(2, "0")}/${String(h.year).slice(-2)}`,
        ranking: h.metrics?.organic?.count || 0,
        top10: h.metrics?.organic?.pos_1 || 0,
      }));
  const isTrackedTrend = trackedTrend.length > 0;

  // Position distribution from the LATEST tracked-keyword snapshot
  const latestSnap = snapshots[0];
  const trackedDistBuckets = (() => {
    const buckets = { "1": 0, "2-3": 0, "4-10": 0, "11-20": 0, "21-50": 0, "51+": 0, "Not found": 0 };
    const res = latestSnap?.results || [];
    for (const r of res) {
      const p = r.position;
      if (p == null) buckets["Not found"] += 1;
      else if (p === 1) buckets["1"] += 1;
      else if (p <= 3) buckets["2-3"] += 1;
      else if (p <= 10) buckets["4-10"] += 1;
      else if (p <= 20) buckets["11-20"] += 1;
      else if (p <= 50) buckets["21-50"] += 1;
      else buckets["51+"] += 1;
    }
    return buckets;
  })();

  // Decide which distribution to render: tracked (when snapshot exists) or domain-wide
  const positionDist = latestSnap
    ? Object.entries(trackedDistBuckets).map(([label, v]) => ({ label, v }))
    : [
        { label: "1", v: ov.pos_1 || 0 },
        { label: "2-3", v: ov.pos_2_3 || 0 },
        { label: "4-10", v: ov.pos_4_10 || 0 },
        { label: "11-20", v: ov.pos_11_20 || 0 },
        { label: "21-50", v: (ov.pos_21_30 || 0) + (ov.pos_31_40 || 0) + (ov.pos_41_50 || 0) },
        { label: "51+", v: (ov.pos_51_60 || 0) + (ov.pos_61_70 || 0) + (ov.pos_71_80 || 0) + (ov.pos_81_90 || 0) + (ov.pos_91_100 || 0) },
      ];

  // ---- Movers: compare latest snapshot with the prior to find top winners + losers,
  // and total up/down/new/lost counts across the tracked keyword set. Full per-bucket
  // lists are also kept so the Up/Down/New/Lost count tiles can be clicked to expand
  // into a dialog listing every affected keyword. ----
  const movers = (() => {
    const a = snapshots[0]?.results || [];
    const b = snapshots[1]?.results || [];
    if (!a.length || !b.length) {
      return { winners: [], losers: [], counts: null, buckets: null, hasData: false };
    }
    const prevByKw = new Map(b.map((r) => [r.keyword.toLowerCase(), r]));
    const items = [];
    const upItems = [], downItems = [], newItems = [], lostItems = [];
    for (const cur of a) {
      const prev = prevByKw.get(cur.keyword.toLowerCase());
      if (!prev) continue;
      const cp = cur.position;
      const pp = prev.position;
      if (cp == null && pp == null) continue;
      let delta;
      let kind;
      const row = { keyword: cur.keyword, cur: cp, prev: pp, url: cur.url };
      if (pp == null && cp != null) {
        delta = 100;
        kind = "new";
        newItems.push({ ...row, delta, kind });
      } else if (pp != null && cp == null) {
        delta = -100;
        kind = "lost";
        lostItems.push({ ...row, delta, kind });
      } else {
        delta = pp - cp;
        kind = "move";
        if (delta > 0) upItems.push({ ...row, delta, kind });
        else if (delta < 0) downItems.push({ ...row, delta, kind });
      }
      if (delta === 0) continue;
      items.push({ ...row, delta, kind });
    }
    // Sorting per-bucket: up by biggest gain first, down by biggest drop first,
    // new/lost by previous→current position (so newly-on-page-1 etc. surface first).
    upItems.sort((x, y) => y.delta - x.delta);
    downItems.sort((x, y) => x.delta - y.delta);
    newItems.sort((x, y) => (x.cur ?? 999) - (y.cur ?? 999));
    lostItems.sort((x, y) => (x.prev ?? 999) - (y.prev ?? 999));
    const winners = upItems.slice(0, 3);
    const losers = downItems.slice(0, 3);
    return {
      winners,
      losers,
      counts: { up: upItems.length, down: downItems.length, new: newItems.length, lost: lostItems.length },
      buckets: { up: upItems, down: downItems, new: newItems, lost: lostItems },
      hasData: true,
    };
  })();
  const moversTimestamp = snapshots[0]?.at;
  const prevTimestamp = snapshots[1]?.at;

  return (
    <div className="space-y-5" data-testid="overview-tab">
      <TabFreshnessNote testid="overview-freshness" savedAt={data?.saved_at}>
        Domain metrics fetched live each visit · Movers + KPI deltas update weekly with the Monday 09:00 UTC rank check.
      </TabFreshnessNote>

      {/* AI Insights teaser strip — renders only when we have a cached insight so
          users notice the new tab without it becoming a noisy "no data" CTA. */}
      {aiTeaser?.insight && (
        <AIInsightsTeaser
          insight={aiTeaser}
          onClick={() => navigate(`/clients/${client.id}/ai-insights`)}
        />
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
        <StatCard label="Ranking Keywords" value={fmtNum(ov.count)} icon={Search} testid="metric-ranking-keywords" hint={ov.is_new ? `+${fmtNum(ov.is_new)} new this month` : null} />
        <StatCard label="Est. Organic Traffic" value={fmtNum(Math.round(ov.etv || 0))} icon={TrendingUp} testid="metric-est-traffic" hint="monthly visits" />
        <StatCard label="Referring Domains" value={fmtNum(referringDomains)} icon={Link2} testid="metric-referring-domains" hint={referringDomainsHint} />
        <StatCard label="Domain Rank" value={domainRank != null ? domainRank : "—"} icon={Award} testid="metric-domain-rank" hint={domainRankHint} />
      </div>

      {/* Movers — top 3 winners / losers from tracked-keyword snapshots */}
      <MoversCard movers={movers} currentAt={moversTimestamp} prevAt={prevTimestamp} />

      {/* GA4 panel — only renders when client.ga4_property_id is set */}
      <GA4Panel client={client} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 card-soft p-5" data-testid="chart-historical">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-display text-lg text-[#1a202c]">
                {isTrackedTrend ? "Tracked keywords overview" : "Ranking keywords overview"}
              </h3>
              <div className="text-xs text-[#48bb78] font-semibold mt-0.5 flex items-center gap-2">
                {isTrackedTrend ? (
                  <>
                    <span className="h-2 w-2 rounded-full bg-[#38b2ac] inline-block" />
                    <span className="text-[#4a5568] font-normal">
                      {trackedTrend.length} snapshot{trackedTrend.length === 1 ? "" : "s"} of your tracked keywords
                    </span>
                  </>
                ) : (
                  <>
                    <span className="text-[#a0aec0] font-normal">
                      Domain-wide history · run rank tracking to chart your tracked set
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trendData}>
                <defs>
                  <linearGradient id="gradKw" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4fd1c5" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#4fd1c5" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradTop10" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#38b2ac" stopOpacity={0.55} />
                    <stop offset="95%" stopColor="#38b2ac" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#edf2f7" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="#a0aec0" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis stroke="#a0aec0" tick={{ fontSize: 10 }} tickFormatter={fmtNum} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, fontSize: 12, boxShadow: "0 4px 12px -2px rgba(45,55,72,0.08)" }} />
                <Area type="monotone" dataKey="ranking" name="Ranking" stroke="#4fd1c5" strokeWidth={2.5} fillOpacity={1} fill="url(#gradKw)" />
                {isTrackedTrend && (
                  <Area type="monotone" dataKey="top10" name="Top 10" stroke="#2c7a7b" strokeWidth={2} fillOpacity={1} fill="url(#gradTop10)" />
                )}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div
          className="p-5 rounded-2xl shadow-[0_4px_12px_-2px_rgba(45,55,72,0.08)]"
          style={{ background: "#1a202c" }}
          data-testid="chart-position-dist"
        >
          <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">
            {latestSnap ? "Tracked position distribution" : "Position distribution"}
          </div>
          <h3 className="font-display text-lg mt-0.5 text-white">
            Where you rank
          </h3>
          <div className="text-[11px] text-[#a0aec0] mt-1">
            {latestSnap
              ? `Latest snapshot · ${new Date(latestSnap.at).toLocaleDateString()}`
              : "Domain-wide · run rank tracking for tracked-set view"}
          </div>
          <div className="h-72 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={positionDist}>
                <CartesianGrid stroke="rgba(255,255,255,0.08)" strokeDasharray="3 3" />
                <XAxis dataKey="label" stroke="#a0aec0" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                <YAxis stroke="#a0aec0" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} tickFormatter={fmtNum} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "#2d3748", border: "1px solid #4a5568", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "#a0aec0" }} />
                <Bar dataKey="v" fill="#4fd1c5" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
        <MovementCard icon={ArrowUp} label="Up" value={movers.counts ? fmtNum(movers.counts.up) : "—"} color="#48bb78" testid="metric-up" hint="vs previous run" onClick={movers.counts?.up ? () => setMoverDialog("up") : null} />
        <MovementCard icon={ArrowDown} label="Down" value={movers.counts ? fmtNum(movers.counts.down) : "—"} color="#f56565" testid="metric-down" hint="vs previous run" onClick={movers.counts?.down ? () => setMoverDialog("down") : null} />
        <MovementCard icon={BarChart3} label="New" value={movers.counts ? fmtNum(movers.counts.new) : "—"} color="#4299e1" testid="metric-new" hint="newly ranking" onClick={movers.counts?.new ? () => setMoverDialog("new") : null} />
        <MovementCard icon={BarChart3} label="Lost" value={movers.counts ? fmtNum(movers.counts.lost) : "—"} color="#a0aec0" testid="metric-lost" hint="dropped out" onClick={movers.counts?.lost ? () => setMoverDialog("lost") : null} />
      </div>

      <MoversBucketDialog
        kind={moverDialog}
        onClose={() => setMoverDialog(null)}
        buckets={movers.buckets}
        currentAt={moversTimestamp}
        prevAt={prevTimestamp}
      />
    </div>
  );
}

function MovementCard({ icon: Icon, label, value, color, testid, hint, onClick }) {
  const clickable = typeof onClick === "function";
  return (
    <button
      type="button"
      onClick={clickable ? onClick : undefined}
      disabled={!clickable}
      className={`card-soft p-5 flex items-center gap-4 text-left w-full transition-all ${
        clickable
          ? "hover:shadow-[0_8px_20px_-4px_rgba(45,55,72,0.12)] hover:-translate-y-0.5 cursor-pointer"
          : "cursor-default"
      }`}
      data-testid={testid}
    >
      <div className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: color + "1a", color }}>
        <Icon className="h-4 w-4" strokeWidth={2.4} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold flex items-center gap-1.5">
          {label}
          {clickable && <ChevronRight className="h-3 w-3 text-[#cbd5e0]" strokeWidth={2.4} />}
        </div>
        <div className="font-mono-tight text-xl text-[#1a202c] mt-0.5">{value}</div>
        {hint && <div className="text-[10px] text-[#a0aec0] font-mono-tight mt-0.5">{hint}</div>}
      </div>
    </button>
  );
}

function MoversCard({ movers, currentAt, prevAt }) {
  const { winners, losers, hasData } = movers;
  const dateLabel = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short" }) : "—";

  return (
    <div className="card-soft" data-testid="movers-card">
      <div className="px-5 py-4 border-b border-[#edf2f7] flex items-end justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-xl bg-[#4fd1c5]/10 text-[#2c7a7b] inline-flex items-center justify-center shrink-0">
            <Sparkles className="h-4 w-4" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">Biggest movers</div>
            <h3 className="font-display text-base mt-0.5 text-[#1a202c]">Top winners &amp; losers vs last run</h3>
          </div>
        </div>
        {hasData && (
          <div className="text-[10px] text-[#a0aec0] font-mono-tight uppercase tracking-widest">
            {dateLabel(prevAt)} → {dateLabel(currentAt)}
          </div>
        )}
      </div>

      {!hasData ? (
        <div className="p-6 text-center text-xs text-[#a0aec0] font-mono-tight" data-testid="movers-empty">
          Need at least 2 rank snapshots to compute movers. Run rank tracking twice (or wait for the weekly cron).
        </div>
      ) : winners.length === 0 && losers.length === 0 ? (
        <div className="p-6 text-center text-xs text-[#a0aec0] font-mono-tight" data-testid="movers-flat">
          No position changes since the previous snapshot.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 divide-x divide-[#edf2f7]">
          <MoverColumn
            kind="winners"
            title="Winners"
            empty="No positive moves this run."
            items={winners}
            accent="#48bb78"
            tone="positive"
          />
          <MoverColumn
            kind="losers"
            title="Losers"
            empty="No drops this run."
            items={losers}
            accent="#f56565"
            tone="negative"
          />
        </div>
      )}
    </div>
  );
}

function MoverColumn({ kind, title, empty, items, accent, tone }) {
  const ChevIcon = tone === "positive" ? ArrowUp : ArrowDown;
  return (
    <div className="p-5" data-testid={`movers-${kind}`}>
      <div className="flex items-center gap-2">
        <span className="h-6 w-6 rounded-md inline-flex items-center justify-center" style={{ background: accent + "1a", color: accent }}>
          <ChevIcon className="h-3 w-3" strokeWidth={2.6} />
        </span>
        <div className="text-[11px] uppercase tracking-widest font-bold" style={{ color: accent }}>{title}</div>
        <div className="ml-auto text-[10px] text-[#a0aec0] font-mono-tight">Top {items.length || 3}</div>
      </div>
      {items.length === 0 ? (
        <div className="mt-4 text-xs text-[#a0aec0] font-mono-tight">{empty}</div>
      ) : (
        <div className="mt-3 space-y-2">
          {items.map((m, i) => (
            <MoverRow key={m.keyword + i} mover={m} tone={tone} accent={accent} index={i} kind={kind} />
          ))}
        </div>
      )}
    </div>
  );
}

function MoverRow({ mover, tone, accent, index, kind }) {
  const { keyword, delta, kind: mKind, cur, prev, url } = mover;
  let chip;
  let chipIcon;
  if (mKind === "new") {
    chip = "NEW";
    chipIcon = <Plus className="h-3 w-3" strokeWidth={2.6} />;
  } else if (mKind === "lost") {
    chip = "DROPPED";
    chipIcon = <Minus className="h-3 w-3" strokeWidth={2.6} />;
  } else {
    const sign = delta > 0 ? "+" : "";
    chip = `${sign}${delta}`;
    chipIcon = tone === "positive" ? <ArrowUp className="h-3 w-3" strokeWidth={2.6} /> : <ArrowDown className="h-3 w-3" strokeWidth={2.6} />;
  }
  const posText = (() => {
    if (mKind === "new") return `now #${cur}`;
    if (mKind === "lost") return `was #${prev}`;
    return `#${prev} → #${cur}`;
  })();

  return (
    <div
      className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-[#f7fafc] transition-colors"
      data-testid={`movers-${kind}-row-${index}`}
    >
      <span className="font-mono-tight text-[10px] text-[#cbd5e0] w-4 text-right">{index + 1}</span>
      <div className="min-w-0 flex-1">
        {url ? (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            title={keyword}
            className="block text-sm text-[#1a202c] truncate hover:text-[#2c7a7b]"
          >
            {keyword}
          </a>
        ) : (
          <div className="text-sm text-[#1a202c] truncate" title={keyword}>{keyword}</div>
        )}
        <div className="text-[10px] text-[#a0aec0] font-mono-tight mt-0.5">{posText}</div>
      </div>
      <span
        className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-md font-mono-tight"
        style={{ background: accent + "1a", color: accent }}
      >
        {chipIcon}
        {chip}
      </span>
    </div>
  );
}


/* ---------- AI Insights teaser strip ---------- */

function AIInsightsTeaser({ insight, onClick }) {
  const ai = insight?.insight || {};
  const wins = ai.wins?.length || 0;
  const concerns = ai.concerns?.length || 0;
  const actions = ai.recommended_actions?.length || 0;
  const headline = (ai.headline || "").trim();

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="ai-insights-teaser"
      className="group w-full text-left card-soft p-4 flex items-center gap-4 hover:shadow-[0_8px_20px_-4px_rgba(56,178,172,0.18)] hover:-translate-y-0.5 transition-all border border-[#4fd1c5]/20 bg-gradient-to-r from-[#4fd1c5]/[0.04] via-white to-white"
    >
      <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#4fd1c5] to-[#319795] text-white flex items-center justify-center shrink-0 shadow-[0_4px_12px_-2px_rgba(56,178,172,0.4)]">
        <Sparkles className="h-4 w-4" strokeWidth={2.2} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <div className="text-[10px] uppercase tracking-widest text-[#2c7a7b] font-bold">AI Insights</div>
          <div className="flex items-center gap-1 text-[10px] font-mono-tight">
            {wins > 0 && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-[#dcfce7] text-[#166534] font-semibold" data-testid="ai-teaser-wins">{wins} {wins === 1 ? "win" : "wins"}</span>
            )}
            {concerns > 0 && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-[#fee2e2] text-[#991b1b] font-semibold" data-testid="ai-teaser-concerns">{concerns} {concerns === 1 ? "concern" : "concerns"}</span>
            )}
            {actions > 0 && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-[#e0f2fe] text-[#075985] font-semibold" data-testid="ai-teaser-actions">{actions} {actions === 1 ? "action" : "actions"}</span>
            )}
          </div>
        </div>
        {headline && (
          <div className="text-sm font-semibold text-[#1a202c] truncate" data-testid="ai-teaser-headline">{headline}</div>
        )}
      </div>
      <div className="inline-flex items-center gap-1.5 text-xs font-bold text-[#2c7a7b] shrink-0 group-hover:gap-2 transition-all">
        Read briefing <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.6} />
      </div>
    </button>
  );
}

/* ---------- Movers bucket dialog (Up / Down / New / Lost click-to-expand) ---------- */

const BUCKET_META = {
  up: { label: "Improved positions", color: "#48bb78", chipBg: "#dcfce7", chipText: "#166534", icon: ArrowUp, hint: "Keywords that gained positions since the previous run" },
  down: { label: "Lost positions", color: "#f56565", chipBg: "#fee2e2", chipText: "#991b1b", icon: ArrowDown, hint: "Keywords that dropped positions since the previous run" },
  new: { label: "Newly ranking", color: "#4299e1", chipBg: "#dbeafe", chipText: "#1e40af", icon: Plus, hint: "Keywords that weren't ranking last run but now appear in Google" },
  lost: { label: "Dropped out", color: "#a0aec0", chipBg: "#e2e8f0", chipText: "#4a5568", icon: Minus, hint: "Keywords that were ranking last run but no longer appear" },
};

function MoversBucketDialog({ kind, onClose, buckets, currentAt, prevAt }) {
  const open = !!kind;
  const meta = kind ? BUCKET_META[kind] : null;
  const items = kind && buckets ? (buckets[kind] || []) : [];
  const Icon = meta?.icon;
  const dateLabel = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "—";

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="bg-white border border-[#e2e8f0] rounded-2xl text-[#1a202c] max-w-2xl max-h-[85vh] shadow-2xl flex flex-col p-0"
        data-testid="movers-bucket-dialog"
      >
        {meta && (
          <>
            <DialogHeader className="px-6 pt-6 pb-4 border-b border-[#edf2f7]">
              <DialogTitle className="flex items-center gap-3">
                <span
                  className="h-9 w-9 rounded-xl inline-flex items-center justify-center"
                  style={{ background: meta.color + "1a", color: meta.color }}
                >
                  <Icon className="h-4 w-4" strokeWidth={2.6} />
                </span>
                <div className="text-left min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold">Biggest movers</div>
                  <div className="font-display text-lg text-[#1a202c]" data-testid="movers-bucket-title">
                    {meta.label} <span className="text-[#a0aec0] font-mono-tight text-sm">· {items.length}</span>
                  </div>
                </div>
              </DialogTitle>
              <div className="text-[11px] text-[#718096] mt-2 font-mono-tight">
                {dateLabel(prevAt)} → {dateLabel(currentAt)}
              </div>
              <div className="text-[11px] text-[#a0aec0] mt-1">{meta.hint}</div>
            </DialogHeader>
            <div className="px-6 py-4 overflow-y-auto" data-testid="movers-bucket-list">
              {items.length === 0 ? (
                <div className="text-center text-xs text-[#a0aec0] font-mono-tight py-10">
                  No keywords in this bucket.
                </div>
              ) : (
                <ul className="divide-y divide-[#edf2f7]">
                  {items.map((m, i) => (
                    <MoversBucketRow key={m.keyword + i} mover={m} kind={kind} meta={meta} index={i} />
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MoversBucketRow({ mover, kind, meta, index }) {
  const { keyword, cur, prev, url, delta } = mover;
  let chipText, posText;
  if (kind === "new") {
    chipText = "NEW";
    posText = cur != null ? `now #${cur}` : "now ranking";
  } else if (kind === "lost") {
    chipText = "DROPPED";
    posText = prev != null ? `was #${prev}` : "previously ranking";
  } else {
    const sign = delta > 0 ? "+" : "";
    chipText = `${sign}${delta}`;
    posText = `#${prev} → #${cur}`;
  }
  return (
    <li
      className="py-2.5 flex items-center gap-3"
      data-testid={`movers-bucket-row-${index}`}
    >
      <span className="font-mono-tight text-[10px] text-[#cbd5e0] w-5 text-right shrink-0">{index + 1}</span>
      <div className="min-w-0 flex-1">
        {url ? (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            title={keyword}
            className="text-sm text-[#1a202c] hover:text-[#2c7a7b] font-medium inline-flex items-center gap-1.5"
          >
            <span className="truncate">{keyword}</span>
            <ExternalLink className="h-3 w-3 text-[#cbd5e0] shrink-0" strokeWidth={2.2} />
          </a>
        ) : (
          <div className="text-sm text-[#1a202c] truncate font-medium" title={keyword}>{keyword}</div>
        )}
        <div className="text-[10px] text-[#a0aec0] font-mono-tight mt-0.5">{posText}</div>
      </div>
      <span
        className="inline-flex items-center px-2 py-1 rounded-md text-[10px] font-bold font-mono-tight shrink-0"
        style={{ background: meta.chipBg, color: meta.chipText }}
      >
        {chipText}
      </span>
    </li>
  );
}
